import { RegistrationPaymentType } from "features/registration-v2/registrationV2Slice";
import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { FormRegistrationData } from "./validationSchemas";
import { isTwikeyMandateRequired } from "features/registration-v2/registrationV2Saga";
import { mapFormDataToUserRegistration } from "./utils";
import { connect } from 'react-redux';
import { ReduxState } from 'types';
import ErrorMessageBox from "components/ErrorMessageBox";
import { formatMessage } from "i18n/CurrentLocale";
import LoadingSpinner from "components/LoadingSpinner";
import imgLogosDebitCards from 'img/financial/logos-debitcards.png';
import imgLogosCreditCards from 'img/financial/logos-creditcards.png';
import { useNavigate } from 'react-router';
import { RegistrationFlowType } from "myParkdUtils/enums";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { GAAction, trackDataLayerEvent } from "myParkdUtils/googleAnalytics";


export const StepPaymentOptions = (props: Props) => {

    const { flowType, formData, registration, setFormStep, onboardedOn } = props;
    const { numCars } = formData;
    const { processing, processed, error, registrationContext } = registration;
    const twikeySignUrl = registrationContext?.twikeySignUrl;

    const [paymentType, setPaymentType] = useState<RegistrationPaymentType | ''>();
    const [showValidation, setShowValidation] = useState<boolean>(false);

    const mandateRequired = isTwikeyMandateRequired(flowType, numCars);
    const navigate = useNavigate();

    useEffect(() => {
        if (!processed) return;

        if (twikeySignUrl) {
            window.location.href = twikeySignUrl;
            return;
        }

        //TODO PD-6034: This logic shouldn't be, if possible, on the frontend.
        if (registrationContext?.telematicProviderType === 'APP') {
            if (onboardedOn) {
                navigate('/vehicles');
            }
            return;
        }

        navigate('/onboarding');

    }, [processed, onboardedOn]);

    /* 
        PD-6034 
        there is still a small glitch, when the loadingspinner stops and getCompanyDetails is fetched again, 
        where you see again the payment step for 1 second.
        We could move this logic to the /onboarding screen, but then we will see that screen for just a second 
        and we would need to hide the telematic account steps + check telematicAccount and add a loading
    */

    const register = ({ skipPayment }: { skipPayment: boolean }) => {
        const formDataWithPaymentInfo = {
            ...formData,
            paymentType: skipPayment ? undefined : paymentType,
            mandateAcceptedByUser: skipPayment ? false : true
        }
        if (!skipPayment && !paymentType) {
            setShowValidation(true);
            return;
        }
        const userRegistration = mapFormDataToUserRegistration(formDataWithPaymentInfo, flowType, props.userLanguage);
        props.registerV2(userRegistration);
        trackDataLayerEvent({ event: GAAction.REGISTRATION_SINGLE_STEP_COMPLETED, registrationStep: 'step_payment' })
    }


    //TODO PD-6034: Having the logic of processing after subscription/registration in this page is a bit confusing and it doesn' respect well the 
    // rules of component cohesion.
    // This component should have logic only for the displaying of payment options, not the redirecting to other pages after registration worked.
    // I think this makes the screen flow harder to track, instead of being lineal, it has many branched.
    // Probably we should have a screen flow more or less like this:
    //  Registration (Step Company Details) -> Registration (StepAccountDetails) -> Registration (StepPaymentOptions) -> Processing registration (new page)
    //   -> (If mandate is required) Tiwkey signature form  -> RegistrationCompleted (could be rename to AfterMandate) -> AfterRegistration
    //   -> (if mandate is not required) AfterRegistration 
    //        -> (if onboarded company) Vehicles  
    //        -> (if not onboarded copany) Onboarding 
    // This would resolve the issue of displaying for short while the payment options, it would display instead the AfterRegistration wher probably there would be just a spinner

    const isFetchingOnboardedOnForAppAccount = !!(processed && registrationContext?.telematicProviderType === 'APP' && !onboardedOn);


    if (isFetchingOnboardedOnForAppAccount) {
        return (
            <div className='pad-2-0'>
                <LoadingSpinner message={<FormattedMessage id="CREATING_ACCOUNT" />} />
            </div>
        )
    }

    if (processing || twikeySignUrl || error) {
        return (
            <div className='pad-2-0'>
                {(processing || twikeySignUrl) && (
                    <>
                        <LoadingSpinner message={<FormattedMessage id="CREATING_ACCOUNT" />} />
                        {paymentType && (
                            <div className="center">
                                <p className="blue-text">
                                    <FormattedMessage id="REGISTRATION_REDIRECTING_TO_TWIKEY" />
                                </p>
                            </div>
                        )}
                    </>
                )}
                {error && <ErrorMessageBox errorMessage={formatMessage({ id: 'ERROR_REGISTRATION_ACCOUNT_CREATION_FAILED' })} />}
            </div>
        )
    }


    return (
        <div className="bg-white">

            <div className='mb-1'>
                <h1 className="title-black"><FormattedMessage id="REGISTRATION_PAYMENT_DETAILS_TITLE" /></h1>
                <p><FormattedMessage id="REGISTRATION_PAYMENT_DETAILS_SUBTITLE" /></p>
            </div>

            <form id='form-step-payment'>
                <div id="payment-options-container" className="radio-group">
                    <label htmlFor="optionBankMandate"
                        className={`payment-option ${paymentType === 'SEPA' ? 'active' : ''}`}
                    >
                        <div className="option-circle" />
                        <div className="option-content">
                            <span className="title-blue" data-testid='OPTION_DIRECT_DEBIT'>
                                {flowType === 'TRACK_AND_TRACE'
                                    ? <FormattedMessage id='PAYMENT_STEP_OPTION_DIRECT_DEBIT_TRACK_AND_TRACE' />
                                    : <FormattedMessage id='PAYMENT_STEP_OPTION_DIRECT_DEBIT_OEM' />
                                }
                            </span>
                        </div>
                        <div className="option-logos">
                            <img src={imgLogosDebitCards} alt="logo's debitcard" />
                        </div>
                    </label>
                    <input
                        type="radio"
                        id="optionBankMandate"
                        name="custom-radio"
                        value="SEPA"
                        checked={paymentType === "SEPA"}
                        onChange={(e) => setPaymentType(e.target.value as RegistrationPaymentType)}
                        required
                    />

                    <label htmlFor="optionCreditCard"
                        className={`payment-option ${paymentType === 'CREDIT_CARD' ? 'active' : ''}`}
                    >
                        <div className="option-circle" />
                        <div className="option-content">
                            <span className="title-blue">
                                {flowType === 'TRACK_AND_TRACE'
                                    ? <FormattedMessage id='PAYMENT_STEP_OPTION_CREDIT_CARD_TRACK_AND_TRACE' />
                                    : <FormattedMessage id='PAYMENT_STEP_OPTION_CREDIT_CARD_OEM' />
                                }
                            </span>
                            <span className="foot-note"><FormattedMessage id='PAYMENT_STEP_OPTION_CREDIT_CARD_FOOTNOTE' /></span>
                        </div>
                        <div className="option-logos">
                            <img src={imgLogosCreditCards} alt="logo's creditcard" />
                        </div>
                    </label>
                    <input
                        type="radio"
                        id="optionCreditCard"
                        name="custom-radio"
                        value="CREDIT_CARD"
                        checked={paymentType === "CREDIT_CARD"}
                        onChange={(e) => setPaymentType(e.target.value as RegistrationPaymentType)}
                        required
                    />
                </div>

                {showValidation && !paymentType && (
                    <ErrorMessageBox errorMessage={formatMessage({ id: 'PAYMENT_STEP_VALIDATION_REQUIRED' })} />
                )}


                <div className="info-box-blue center mb-1">
                    <p className="t-17-500-blue"><FormattedMessage id="REGISTRATION_PAYMENT_DETAILS_INFOBOX_A" /></p>
                    <p className="t-17-500-blue"><FormattedMessage id="REGISTRATION_PAYMENT_DETAILS_INFOBOX_B" /></p>
                </div>


                <div className='flex-space-between'>
                    <button type="button" className="btn-back" onClick={() => setFormStep(2)}>
                        <FontAwesomeIcon icon={faArrowLeft} size='xs' />
                        <span><FormattedMessage id="GLOBAL_CTA_BACK" /></span>
                    </button>
                    <div className="buttons-group-v2">
                        {!mandateRequired && (
                            <button type="button" className="btn-new-outlined" onClick={() => register({ skipPayment: true })}>
                                <FormattedMessage id='PAYMENT_STEP_BTN_SKIP' />
                            </button>
                        )}
                        <button type="button" className="btn-new" onClick={() => register({ skipPayment: false })}>
                            <FormattedMessage id='CREATE_ACCOUNT' />
                            <FontAwesomeIcon icon={faArrowRight} size='xs' />
                        </button>
                    </div>
                </div>

            </form>
        </div>
    )
}


type OwnProps = {
    formData: FormRegistrationData,
    flowType: RegistrationFlowType,
    setFormStep: Function,
    registerV2: Function
}
type StateProps = ReturnType<typeof mapStateToProps>;
type Props = StateProps & OwnProps;


function mapStateToProps(state: ReduxState) {
    return {
        registration: state?.registration,
        userLanguage: state?.userLanguage?.userLanguage,
        token: state?.auth?.token,
        onboardedOn: state?.companyDetails?.companyDetails?.onboardedOn ?? false
    };
}

export default connect(mapStateToProps, null)(StepPaymentOptions);